import {
  DEFAULT_PARAMS,
  type EngineParams,
  type NoteEvent,
  type RingNode,
  type ViewState,
  type WeatherSnapshot,
} from '@windchime/shared';
import { MODES, modeIndexForTemp } from './music.js';
import { mulberry32 } from './rng.js';
import { turbulence } from './noise.js';

const DEG = Math.PI / 180;
/** puck radius at which effective clapper mass == clapperMass (matches DEFAULT_PARAMS.clapperRadius) */
const REF_CLAPPER_RADIUS = 0.12;

export interface EngineOptions {
  /** PRNG seed; same seed + inputs => identical output */
  seed?: number;
  params?: Partial<EngineParams>;
}

/**
 * The virtual wind chime, with no audio, rendering, network, or DOM.
 *
 *   setWeather(snapshot)  feed normalised weather (sliders or the gateway)
 *   tick(dt)              advance the simulation; returns notes fired this tick
 *   onNote(cb)            subscribe to the Tier A note stream (audio + hardware)
 *   getViewState()        read state for a renderer
 */
export class Engine {
  private params: EngineParams;
  private rng: () => number;
  private weather: WeatherSnapshot = {
    windSpeed: 0,
    windDirFrom: 0,
    gust: 1,
    tempF: 70,
    localHour: 12,
  };
  private listeners = new Set<(e: NoteEvent) => void>();

  private simT = 0;
  private plate = { x: 0, y: 0, vx: 0, vy: 0 };
  private ring: RingNode[] = [];
  private modeIdx = 1; // Ionian anchor
  private modeOverride: number | null = null;
  private modeCooldown = 0;
  private lastMidi = -1;
  private instW = 0;
  private activity = 1;

  // --- contact-model state (used when params.strikeModel === 'contact') ---
  private sail = { x: 0, y: 0, vx: 0, vy: 0, angle: 0, angV: 0 };
  private tubeDyn: Array<{ rho: number; rhoV: number; delta: number; deltaV: number }> = [];
  private inContact: boolean[] = [];
  private lastHitT: number[] = [];
  private barFatigue: number[] = []; // per-tube loudness energy, 1 = fully rested
  private physAcc = 0;
  private lastContact: { bar: number; impact: number; tSim: number } | null = null;

  constructor(opts: EngineOptions = {}) {
    this.params = { ...DEFAULT_PARAMS, ...(opts.params ?? {}) };
    this.rng = mulberry32(opts.seed ?? 0x9e3779b9);
    this.rebuildRing(this.modeIdx);
  }

  setWeather(s: Partial<WeatherSnapshot>): void {
    this.weather = { ...this.weather, ...s };
  }

  setParams(p: Partial<EngineParams>): void {
    const reRing =
      p.rootPc !== undefined || p.noteCount !== undefined || p.rootOctave !== undefined;
    this.params = { ...this.params, ...p };
    if (reRing) this.rebuildRing(this.modeIdx);
  }

  /** Pass a mode name to pin it, or null to return to temperature-driven auto. */
  setModeOverride(name: string | null): void {
    if (name == null) {
      this.modeOverride = null;
      return;
    }
    const i = MODES.findIndex((m) => m.name.toLowerCase() === name.toLowerCase());
    if (i >= 0) {
      this.modeOverride = i;
      this.setMode(i);
    }
  }

  /** Subscribe to note events. Returns an unsubscribe function. */
  onNote(cb: (e: NoteEvent) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  getViewState(): ViewState {
    const p = this.params;
    const contact = p.strikeModel === 'contact';
    return {
      plate: { x: this.plate.x, y: this.plate.y },
      clapper: { x: this.plate.x, y: this.plate.y },
      sail: { x: this.sail.x, y: this.sail.y },
      sailAngle: this.sail.angle,
      swingAzimuth: Math.atan2(this.plate.y, this.plate.x),
      swingMagnitude: Math.min(1, Math.hypot(this.plate.x, this.plate.y)),
      arcHalfWidth: p.arcHalfWidth,
      barRadius: p.barRadius,
      clapperReach: p.clapperReach,
      clapperRadius: p.clapperRadius,
      barHalfWidth: p.barHalfWidth,
      // cord length L = g/ω² (metres); lets the side view draw the cords to scale
      cordLen: {
        clapper: p.gravity / (p.clapperOmega * p.clapperOmega),
        sail: p.gravity / (p.sailOmega * p.sailOmega),
      },
      strikeModel: p.strikeModel,
      lastContact: this.lastContact,
      instWind: this.instW,
      activity: this.activity,
      rootPc: p.rootPc,
      mode: { name: MODES[this.modeIdx].name, index: this.modeIdx },
      ring: this.ring.map((n, j) => {
        const td = this.tubeDyn[j];
        const delta = contact && td ? td.delta : 0;
        const rho = contact && td ? td.rho : 0;
        return {
          midi: n.midi,
          angle: n.angle + delta, // live (swayed) angle in contact mode
          restAngle: n.angle,
          radius: p.barRadius + rho,
          isRoot: n.isRoot,
          flash: n.flash,
        };
      }),
    };
  }

  /** Advance the simulation by dt seconds. Returns the notes fired this tick. */
  tick(dt: number): NoteEvent[] {
    const p = this.params;
    this.simT += dt;

    // mode from temperature unless overridden; cooldown stops it flapping
    this.modeCooldown -= dt;
    if (this.modeOverride == null) {
      const want = modeIndexForTemp(this.weather.tempF);
      if (want !== this.modeIdx && this.modeCooldown <= 0) {
        this.setMode(want);
        this.modeCooldown = p.modeChangeCooldownS;
      }
    }

    // instantaneous wind with gust turbulence
    const tb = turbulence(this.simT * p.turbulenceFreq);
    this.instW = Math.max(
      0,
      this.weather.windSpeed * (1 + (this.weather.gust - 1) * p.gustSensitivity * tb),
    );

    // activity ceiling from local time of day (trough ~4am, peak ~4pm)
    this.activity = timeFactor(this.weather.localHour);

    const out: NoteEvent[] = [];
    if (p.strikeModel === 'contact') this.tickContact(dt, out);
    else this.tickPoisson(dt, out);
    return out;
  }

  // --- internals -----------------------------------------------------------

  /** v1 strike engine: damped-spring plate + Poisson scheduler (unchanged behavior). */
  private tickPoisson(dt: number, out: NoteEvent[]): void {
    const p = this.params;
    // plate pushed downwind; spring + damping pull it back to centre
    const a = (this.weather.windDirFrom + 180) * DEG;
    const ux = Math.sin(a);
    const uy = -Math.cos(a);
    const force = this.instW / 80;
    const jitter = 0.18 * turbulence(this.simT * 2 * p.turbulenceFreq + 10);
    const fx = ux * (force + jitter) * p.driveScale - p.spring * this.plate.x - p.damping * this.plate.vx;
    const fy = uy * (force + jitter) * p.driveScale - p.spring * this.plate.y - p.damping * this.plate.vy;
    this.plate.vx += fx * dt;
    this.plate.vy += fy * dt;
    this.plate.x += this.plate.vx * dt;
    this.plate.y += this.plate.vy * dt;

    // decay strike glows
    const decay = Math.pow(0.0008, dt);
    for (const n of this.ring) n.flash *= decay;

    // Poisson strike process, rate from instantaneous wind * activity
    if (this.instW >= p.lullSilenceKmh) {
      const rate =
        Math.min(p.maxRate, (this.instW / 80) * p.maxRate) * this.activity * p.densityScale;
      let prob = rate * dt;
      let guard = 0;
      while (prob > 0 && guard < 4) {
        if (this.rng() < Math.min(prob, 1)) {
          const e = this.fireStrike();
          if (e) out.push(e);
        }
        prob -= 1;
        guard++;
      }
    }
  }

  /**
   * v2 strike engine: a wind sail drives the clapper (double pendulum); the clapper
   * strikes the hanging tubes, which recoil and sway. Impact velocity is the
   * clapper↔tube relative speed; the clapper bounces (residual rebound → chatter).
   * Deterministic via a fixed substep; see docs/05_strike_physics_spec.md.
   */
  private tickContact(dt: number, out: NoteEvent[]): void {
    const p = this.params;

    // strike glows decay once per tick (same envelope as the Poisson path)
    const decay = Math.pow(0.0008, dt);
    for (const n of this.ring) n.flash *= decay;

    // wind as a velocity vector blowing TOWARD downwind, with a slowly-swirling
    // direction (gusts wander) so the clapper sweeps across tubes; lull + time-of-day gate it
    const a =
      (this.weather.windDirFrom + 180) * DEG +
      p.windWander * turbulence(this.simT * p.turbulenceFreq * 0.5 + 5);
    const drive = (this.instW >= p.lullSilenceKmh ? this.instW * p.windDrive : 0) * this.activity;
    const wx = Math.sin(a) * drive;
    const wy = -Math.cos(a) * drive;

    // fixed-timestep substeps (deterministic + tunnel-proof); carry the remainder
    const h = p.physicsSubstepS;
    this.physAcc = Math.min(this.physAcc + dt, 0.1); // bound work on a pathological dt
    let guard = 0;
    while (this.physAcc >= h && guard < 64) {
      this.physicsSubstep(h, wx, wy, out);
      this.physAcc -= h;
      guard++;
    }
  }

  /** One fixed integration + contact step of the sail/clapper/tube system. */
  private physicsSubstep(h: number, wx: number, wy: number, out: NoteEvent[]): void {
    const p = this.params;
    const c = this.plate; // the clapper
    const s = this.sail;

    // wind sail: quadratic relative-wind drag, pendulum toward the clapper, damping
    const relx = wx - s.vx;
    const rely = wy - s.vy;
    const relMag = Math.hypot(relx, rely);
    let drag = p.sailDrag;
    if (p.sailFeather > 0) {
      const wMag = Math.hypot(wx, wy);
      const sMag = Math.hypot(s.vx, s.vy);
      if (wMag > 1e-6 && sMag > 1e-6) {
        const align = Math.max(0, (s.vx * wx + s.vy * wy) / (sMag * wMag));
        drag *= 1 - p.sailFeather * align; // sail running with the wind catches less
      }
    }
    // the sail is a rectangular plate that also SPINS about the cord. The wind spins it up to a
    // terminal rate set by wind strength (faster in stronger wind), with the spin↔swing coupling
    // and gust turbulence keeping the rate uneven. As it turns it alternately faces the wind
    // (catches) and slices it edge-on (slips), so the catch pulses sub-second — the rotation's
    // "seeming randomness" in the drive. A lift term steers slightly with the angle of attack.
    let liftx = 0;
    let lifty = 0;
    if (relMag > 1e-6) {
      const relAng = Math.atan2(rely, relx);
      // wind-driven spin: torque ∝ wind, damping sets the terminal rate (windmill-like)
      const torque = p.sailTorque * relMag - p.sailRotDamping * s.angV;
      s.angV = clamp(s.angV + torque * h, -30, 30);
      s.angle += s.angV * h;
      let aoa = relAng - s.angle;
      aoa = Math.atan2(Math.sin(aoa), Math.cos(aoa)); // angle of attack, wrapped to [-π, π]
      // broadside (facing wind) catches full; edge-on (sliced) catches little
      const face = Math.abs(Math.cos(aoa));
      drag *= 1 - p.sailFlutter * (1 - face);
      // small lift ∝ sin(2·aoa) perpendicular to the flow, wobbling the drive direction
      const lift = (p.sailLift * relMag * relMag * Math.sin(2 * aoa)) / p.sailMass;
      liftx = (-rely / relMag) * lift;
      lifty = (relx / relMag) * lift;
    }
    const so2 = p.sailOmega * p.sailOmega;
    const aSx = (drag * relMag * relx) / p.sailMass + liftx - so2 * (s.x - c.x) - p.sailDamping * s.vx;
    const aSy = (drag * relMag * rely) / p.sailMass + lifty - so2 * (s.y - c.y) - p.sailDamping * s.vy;
    s.vx += aSx * h;
    s.vy += aSy * h;
    s.x += s.vx * h;
    s.y += s.vy * h;
    // the sail cord can't stretch: clamp |sail − clapper| to its length (taut cord)
    clampCord(s, c.x, c.y, c.vx, c.vy, p.sailReach);

    // clapper: pulled toward the sail (cord reaction ∝ mS/mC), own pendulum, small wind drag.
    // Effective mass grows with puck size: a bigger puck is heavier, so the same sail pull moves
    // it less — it swings slower and needs more wind. Kept ~linear (not area²) so a large puck is
    // still drivable in good wind and can deliver its wider-reach "more tubes at once" payoff.
    const mC = p.clapperMass * (p.clapperRadius / REF_CLAPPER_RADIUS);
    const co2 = p.clapperOmega * p.clapperOmega;
    const cwx = wx - c.vx;
    const cwy = wy - c.vy;
    const cwMag = Math.hypot(cwx, cwy);
    const coupling = (p.sailMass / mC) * so2;
    const aCx =
      coupling * (s.x - c.x) - co2 * c.x + (p.clapperDrag * cwMag * cwx) / mC - p.clapperDamping * c.vx;
    const aCy =
      coupling * (s.y - c.y) - co2 * c.y + (p.clapperDrag * cwMag * cwy) / mC - p.clapperDamping * c.vy;
    c.vx += aCx * h;
    c.vy += aCy * h;
    c.x += c.vx * h;
    c.y += c.vy * h;
    // the clapper cord can't stretch: clamp its reach. This bounds the swing and is what
    // lets a steady wind hold the clapper out among the tubes instead of flinging it away.
    clampCord(c, 0, 0, 0, 0, p.clapperReach);

    // tubes: each a damped pendulum in radial (recoil ρ) + tangential (sway δ) offset
    const to2 = p.tubeOmega * p.tubeOmega;
    const n = this.ring.length;
    for (let j = 0; j < n; j++) {
      const td = this.tubeDyn[j];
      td.rhoV += (-to2 * td.rho - p.tubeDamping * td.rhoV) * h;
      td.rho += td.rhoV * h;
      td.deltaV += (-to2 * td.delta - p.tubeDamping * td.deltaV) * h;
      td.delta += td.deltaV * h;
    }
    if (n === 0) return;

    // contact: the clapper is a PUCK of radius clapperRadius, so its surface reaches
    // rc + clapperRadius and its angular span widens by asin(clapperRadius/liveR). A bigger
    // puck therefore overlaps several adjacent tubes at once — strike each one (per-tube
    // refractory/fatigue) — while the rebound comes off the single deepest tube.
    const rc = Math.hypot(c.x, c.y);
    if (rc <= 1e-9) {
      for (let k = 0; k < n; k++) this.inContact[k] = false;
      return;
    }
    const thc = Math.atan2(c.y, c.x);
    const nrx = c.x / rc; // outward radial unit (clapper heading)
    const nry = c.y / rc;
    const reach = rc + p.clapperRadius; // the puck's leading surface radius
    let deepest = -1;
    let deepestPen = -Infinity;
    let deepLiveR = p.barRadius;
    for (let j = 0; j < n; j++) {
      const node = this.ring[j];
      const td = this.tubeDyn[j];
      const liveAngle = node.angle + td.delta;
      const liveR = p.barRadius + td.rho;
      const faceSpan = p.barHalfWidth + Math.asin(clamp(p.clapperRadius / Math.max(liveR, 1e-6), 0, 1));
      const overlapping = reach >= liveR - 1e-4 && angDiff(thc, liveAngle) < faceSpan;
      if (!overlapping) {
        this.inContact[j] = false;
        continue;
      }
      // strike on entry into this tube's face
      if (!this.inContact[j]) {
        const car = Math.cos(liveAngle);
        const sar = Math.sin(liveAngle);
        const vtubeX = td.rhoV * car - liveR * td.deltaV * sar;
        const vtubeY = td.rhoV * sar + liveR * td.deltaV * car;
        const vrelx = c.vx - vtubeX;
        const vrely = c.vy - vtubeY;
        const vrad = vrelx * nrx + vrely * nry; // + = moving into the tube (radial)
        const vtan = -vrelx * nry + vrely * nrx; // sweeping speed
        const impact = Math.sqrt(Math.max(0, vrad) ** 2 + (p.tangentialCoupling * vtan) ** 2);
        if (this.simT - this.lastHitT[j] >= p.barRefractoryS && impact >= p.minImpactVel) {
          // soft-knee impact→velocity: no pile-up at the ceiling, so velocity tracks impact.
          const raw = p.velFloor + (p.velCeil - p.velFloor) * Math.tanh(p.velGain * impact);
          // per-tube fatigue gives a rattle train a natural decay: the tube recovers toward
          // full loudness with time constant rehitTauS and spends a fraction (to rehitFloor)
          // per strike, so each rapid repeat is softer and full loudness returns after a rest.
          const dtHit = this.simT - this.lastHitT[j];
          const recovered = 1 - (1 - this.barFatigue[j]) * Math.exp(-dtHit / p.rehitTauS);
          let vel = clamp(raw * recovered, 0, p.velCeil);
          if (p.contactJitter > 0) {
            vel = clamp(vel + (this.rng() - 0.5) * p.contactJitter, p.velFloor, p.velCeil);
          }
          out.push(this.emit(node, vel));
          this.barFatigue[j] = recovered * p.rehitFloor; // this strike spends energy
          this.lastHitT[j] = this.simT;
          this.lastContact = { bar: j, impact, tSim: this.simT };
          // tube recoils outward (+ a tangential nudge in the glancing direction)
          td.rhoV += p.tubeRecoil * impact;
          td.deltaV += p.tubeTangentialRecoil * p.tubeRecoil * impact * Math.sign(vtan);
        }
      }
      this.inContact[j] = true;
      const pen = reach - liveR;
      if (pen > deepestPen) {
        deepestPen = pen;
        deepest = j;
        deepLiveR = liveR;
      }
    }
    // residual bounce off the single deepest tube on a radial-outward hit (the chatter rebound)
    if (deepest >= 0) {
      const td = this.tubeDyn[deepest];
      const liveAngle = this.ring[deepest].angle + td.delta;
      const car = Math.cos(liveAngle);
      const sar = Math.sin(liveAngle);
      const vtubeX = td.rhoV * car - deepLiveR * td.deltaV * sar;
      const vtubeY = td.rhoV * sar + deepLiveR * td.deltaV * car;
      const vrad = (c.vx - vtubeX) * nrx + (c.vy - vtubeY) * nry;
      const vtan = -(c.vx - vtubeX) * nry + (c.vy - vtubeY) * nrx;
      if (vrad > 0) {
        const nvrad = -p.restitution * vrad;
        const nvtan = (1 - p.tangentialFriction) * vtan;
        c.vx = nvrad * nrx - nvtan * nry + vtubeX;
        c.vy = nvrad * nry + nvtan * nrx + vtubeY;
        const centerR = Math.max(0, deepLiveR - p.clapperRadius); // puck surface rests on the tube face
        c.x = centerR * nrx;
        c.y = centerR * nry;
      }
    }
    // the clapper cord still limits the centre's reach even after a contact projection
    clampCord(c, 0, 0, 0, 0, p.clapperReach);
  }

  private setMode(i: number): void {
    this.modeIdx = i;
    this.rebuildRing(i);
  }

  private rebuildRing(modeIdx: number): void {
    const p = this.params;
    // A real wind chime: the root in a fixed octave plus the first `noteCount`
    // scale degrees — one octave, no octave jumps. Taking the lowest degrees also
    // drops each mode's harsh 7th, the note that clangs against the root.
    const rootMidi = 12 * (p.rootOctave + 1) + p.rootPc;
    const pitches = MODES[modeIdx].semis.slice(0, p.noteCount).map((s) => rootMidi + s);
    const n = pitches.length;
    const prevFlash = new Map(this.ring.map((nd) => [nd.midi, nd.flash]));
    this.ring = pitches.map((midi, i) => ({
      midi,
      angle: -Math.PI / 2 + (2 * Math.PI * i) / n,
      isRoot: ((midi % 12) + 12) % 12 === p.rootPc,
      flash: prevFlash.get(midi) ?? 0,
    }));
    // (re)size the contact-model per-tube state to match the ring
    this.tubeDyn = this.ring.map(() => ({ rho: 0, rhoV: 0, delta: 0, deltaV: 0 }));
    this.inContact = this.ring.map(() => false);
    this.lastHitT = this.ring.map(() => -Infinity);
    this.barFatigue = this.ring.map(() => 1);
  }

  private fireStrike(): NoteEvent | null {
    if (this.ring.length === 0) return null;
    const p = this.params;
    const az = Math.atan2(this.plate.y, this.plate.x);
    const arc = p.arcHalfWidth;

    // candidate notes within the active arc, weighted toward the swing centre
    const cand: Array<[RingNode, number]> = [];
    for (const n of this.ring) {
      const d = angDiff(n.angle, az);
      if (d < arc) {
        let w = Math.pow(1 - d / arc, 1.6);
        if (n.midi === this.lastMidi) w *= 0.25; // discourage repeats
        cand.push([n, w]);
      }
    }
    if (cand.length === 0) return null;

    let tot = 0;
    for (const c of cand) tot += c[1];
    let r = this.rng() * tot;
    let pick = cand[0][0];
    for (const c of cand) {
      r -= c[1];
      if (r <= 0) {
        pick = c[0];
        break;
      }
    }

    const base = 0.18 + 0.6 * (this.instW / 80);
    const vJit = (this.rng() - 0.5) * 0.4 * this.weather.gust;
    const velocity = clamp(base + vJit, p.velFloor, p.velCeil);

    this.lastMidi = pick.midi;
    return this.emit(pick, velocity);
  }

  /**
   * Set the strike glow, build the Tier A NoteEvent, and notify listeners.
   * The single emission seam shared by both strike models (and the hardware sink).
   */
  private emit(node: RingNode, velocity: number): NoteEvent {
    // In contact mode the glow tracks loudness, so a faint strike reads as a faint flash
    // (a quiet hit no longer looks identical to a slam). Poisson keeps its full-bright
    // pulse — the approved prototype look.
    node.flash = this.params.strikeModel === 'contact' ? clamp(velocity, 0.06, 1) : 1;
    const e: NoteEvent = { midi: node.midi, velocity, tSim: this.simT };
    for (const cb of this.listeners) cb(e);
    return e;
  }
}

function angDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % (2 * Math.PI);
  return d > Math.PI ? 2 * Math.PI - d : d;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Taut-cord constraint: keep |body − anchor| ≤ maxLen, killing only the outward radial velocity. */
function clampCord(
  b: { x: number; y: number; vx: number; vy: number },
  ax: number,
  ay: number,
  avx: number,
  avy: number,
  maxLen: number,
): void {
  const dx = b.x - ax;
  const dy = b.y - ay;
  const r = Math.hypot(dx, dy);
  if (r > maxLen && r > 1e-9) {
    const nx = dx / r;
    const ny = dy / r;
    b.x = ax + nx * maxLen;
    b.y = ay + ny * maxLen;
    const vout = (b.vx - avx) * nx + (b.vy - avy) * ny;
    if (vout > 0) {
      b.vx -= vout * nx;
      b.vy -= vout * ny;
    }
  }
}

/** Single smooth hump over the day: trough ~04:00, peak ~16:00. */
function timeFactor(hour: number): number {
  const phase = (((((hour - 4) % 24) + 24) % 24)) / 24;
  return 0.18 + 0.82 * Math.max(0, Math.sin(Math.PI * phase));
}
